"use client";

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  type ReactNode,
} from "react";
import { useSSE, type SSEConnectionState } from "@/hooks/use-sse";
import { api, ApiError } from "@/lib/api";
import type {
  AnalysisState,
  AnalysisSection,
  SSEStatusPayload,
  SSESectionPayload,
  SSECardsPayload,
} from "@/types/analysis";

// ── 리듀서 액션 ──
type AnalysisAction =
  | { type: "START_ANALYSIS"; ticker: string; jobId: string }
  | { type: "SET_STATUS"; status: AnalysisState["status"] }
  | { type: "SET_SECTION"; payload: SSESectionPayload }
  | { type: "SET_CARDS"; sectionId: string; cards: AnalysisSection["cards"] }
  | { type: "DONE" }
  | { type: "ERROR"; message: string }
  | { type: "RESET" };

const initialState: AnalysisState = {
  status: "idle",
  jobId: null,
  ticker: null,
  sections: [],
  currentSectionIndex: 0,
  totalSections: 0,
  error: null,
};

function analysisReducer(state: AnalysisState, action: AnalysisAction): AnalysisState {
  switch (action.type) {
    case "START_ANALYSIS":
      return {
        ...initialState,
        status: "loading",
        ticker: action.ticker,
        jobId: action.jobId,
      };

    case "SET_STATUS":
      return { ...state, status: action.status };

    case "SET_SECTION": {
      const { id, title, index, total } = action.payload;
      const existing = state.sections.find((s) => s.id === id);
      const sections = existing
        ? state.sections.map((s) =>
            s.id === id ? { ...s, status: "running" as const } : s
          )
        : [
            ...state.sections.map((s) =>
              s.status === "running" ? { ...s, status: "done" as const } : s
            ),
            { id, title, status: "running" as const },
          ];
      return {
        ...state,
        status: "streaming",
        sections,
        currentSectionIndex: index,
        totalSections: total,
      };
    }

    case "SET_CARDS": {
      const sections = state.sections.map((s) =>
        s.id === action.sectionId ? { ...s, cards: action.cards } : s
      );
      return { ...state, sections };
    }

    case "DONE":
      return {
        ...state,
        status: "done",
        sections: state.sections.map((s) => ({ ...s, status: "done" as const })),
      };

    case "ERROR":
      return { ...state, status: "error", error: action.message };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

// ── Context 인터페이스 ──
interface AnalysisContextValue {
  state: AnalysisState;
  sseConnectionState: SSEConnectionState;
  /** 분석 시작: ticker 제출 → API 호출 → SSE 연결. 성공 시 jobId 반환, 실패 시 null */
  startAnalysis: (ticker: string) => Promise<string | null>;
  /** 분석 초기화 */
  reset: () => void;
}

const AnalysisContext = createContext<AnalysisContextValue | null>(null);

// ── Provider ──
export function AnalysisProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(analysisReducer, initialState);

  const { connectionState, connect, disconnect } = useSSE({
    onStatus(data: SSEStatusPayload) {
      if (data.status === "streaming" || data.status === "running") {
        dispatch({ type: "SET_STATUS", status: "streaming" });
      }
    },
    onSection(data: SSESectionPayload) {
      dispatch({ type: "SET_SECTION", payload: data });
    },
    onCards(data) {
      dispatch({ type: "SET_CARDS", sectionId: data.id, cards: data.cards });
    },
    onDone() {
      dispatch({ type: "DONE" });
    },
    onError(data) {
      dispatch({ type: "ERROR", message: data.message });
    },
    onConnectionError() {
      dispatch({ type: "ERROR", message: "서버 연결이 끊어졌습니다. 다시 시도해주세요." });
    },
  });

  const startAnalysis = useCallback(
    async (ticker: string): Promise<string | null> => {
      // 기존 연결 정리
      disconnect();

      // POST /api/ticker/report 로 분석 요청
      dispatch({ type: "START_ANALYSIS", ticker, jobId: "" });

      try {
        const data = await api<{ jobId: string }>("/api/ticker/report", {
          method: "POST",
          body: { ticker },
        });

        dispatch({ type: "START_ANALYSIS", ticker, jobId: data.jobId });
        // 보고서 생성 = 30 credits 차감 → 사이드바 즉시 갱신
        // detail.delta.credits 로 낙관적 업데이트 + 서버 sync 둘 다 트리거
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("credits-updated", {
            detail: { delta: { credits: -30 } },
          }));
        }
        // SSE 스트림 연결
        connect(data.jobId);
        return data.jobId;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          dispatch({ type: "ERROR", message: "__LOGIN_REQUIRED__" });
          return null;
        }
        // 크레딧 부족 429는 호출자가 처리하도록 re-throw
        if (err instanceof ApiError && err.status === 429 && err.data?.code === "CREDITS_EXHAUSTED") {
          dispatch({ type: "RESET" });
          throw err;
        }
        // 존재하지 않는 티커 400은 호출자가 서버 메시지를 토스트로 띄우도록 re-throw
        if (err instanceof ApiError && err.status === 400) {
          dispatch({ type: "RESET" });
          throw err;
        }
        const msg = err instanceof ApiError ? err.message : "네트워크 오류가 발생했습니다.";
        dispatch({ type: "ERROR", message: msg });
        return null;
      }
    },
    [connect, disconnect]
  );

  const reset = useCallback(() => {
    disconnect();
    dispatch({ type: "RESET" });
  }, [disconnect]);

  return (
    <AnalysisContext.Provider
      value={{ state, sseConnectionState: connectionState, startAnalysis, reset }}
    >
      {children}
    </AnalysisContext.Provider>
  );
}

// ── Hook ──
export function useAnalysis(): AnalysisContextValue {
  const ctx = useContext(AnalysisContext);
  if (!ctx) {
    throw new Error("useAnalysis는 AnalysisProvider 안에서 사용해야 합니다.");
  }
  return ctx;
}
